import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  NullableDropdownGroup,
  NullableDropdownItem,
} from '@wix/da-ds/pkg/Dropdown/types';
import { useTableActionsRegistry } from './useTableActionsRegistry';
import {
  getActiveSection,
  getIsDraftsSection,
  getStudioParams,
  getTableLayout,
} from '../../../selectors/studio';
import { StudioSection, TableLayout } from '../../../types/store';
import { TableAction, TableActionsContext } from '../types';

const MAIN_CTA_WIDTH_ESTIMATE = 120;
const MAIN_CTA_WIDTH_ESTIMATE_ORIGINALS = 160;
const BUTTON_SIZE = 32;
const BUTTON_GAP = 24;

export function useTableActions(context: TableActionsContext) {
  const { mainCtaFromProps, columnWidth, onlyContextMenu } = context;
  const actions = useTableActionsRegistry(context);
  const activeSection = useSelector(getActiveSection);
  const isDraftsSection = useSelector(getIsDraftsSection);
  const isGridLayout = useSelector(getTableLayout) === TableLayout.GRID;
  const { contentType } = useSelector(getStudioParams);

  const mainCtaWidthEstimate =
    activeSection === StudioSection.PRODUCTS_ORIGINAL
      ? MAIN_CTA_WIDTH_ESTIMATE_ORIGINALS
      : MAIN_CTA_WIDTH_ESTIMATE;

  const [availableActions, mainCtaAction] = useMemo(() => {
    switch (activeSection) {
      case StudioSection.PRODUCTS_EXCLUSIVE:
        return [
          [
            actions.boost,
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.share,
            actions.sell,
            actions.delete,
            actions.unpublish,
          ],
          actions.sell,
        ];
      case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
        return [
          [
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.sell,
            actions.schedule,
            actions.delete,
          ],
          actions.sell,
        ];
      case StudioSection.PRODUCTS_COMMISSION:
        return [
          [
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.share,
            actions.delete,
          ],
          actions.edit,
        ];
      case StudioSection.PRODUCTS_ORIGINAL:
        return [
          [
            actions.boost,
            actions.originalStatus,
            actions.edit,
            actions.copyLink,
            actions.view,
            actions.delete,
          ],
          actions.originalStatus,
        ];
      case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
        return [[actions.delete], actions.editAndPublish];
      case StudioSection.PRODUCTS_PCP:
        return [
          [
            actions.boost,
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.share,
            actions.addToGroup,
            actions.sell,
            actions.delete,
            actions.unpublish,
          ],
          actions.sell,
        ];
      case StudioSection.PRODUCTS_PCP_UNLISTED:
        return [
          [
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.sell,
            actions.schedule,
            actions.delete,
          ],
          actions.sell,
        ];
      case StudioSection.PRODUCTS_TIER:
        return [
          [
            actions.boost,
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.share,
            actions.delete,
          ],
          null,
        ];
      case StudioSection.PRODUCTS_TIER_UNLISTED:
        return [[actions.edit, actions.publishTier, actions.delete], null];
      case StudioSection.APPS:
      case StudioSection.APPS_UNLISTED:
        return [
          [
            actions.edit,
            actions.view,
            actions.unpublish,
            actions.reorder,
            actions.copyLink,
            actions.addToGroup,
            actions.share,
            actions.delete,
          ],
          actions.edit,
        ];
      default:
        let mainCta: TableAction | null = null;
        if (activeSection === StudioSection.PUBLISHED) {
          mainCta = contentType === 'posts' ? actions.edit : actions.sell;
        } else if (
          [StudioSection.PLAYGROUND, StudioSection.STASH].includes(
            activeSection
          )
        ) {
          mainCta = actions.view;
        } else if (isDraftsSection) {
          mainCta = actions.edit;
        }

        return [
          [
            actions.boost,
            actions.copyLink,
            actions.view,
            actions.edit,
            actions.share,
            actions.reorder,
            actions.moveToFolder,
            actions.addToGroup,
            actions.delete,
            actions.dreamupEdit,
            actions.schedule,
            actions.unpublish,
            actions.sell,
          ],
          mainCta,
        ];
    }
  }, [actions, activeSection, contentType, isDraftsSection]);

  const mainCta = useMemo(
    () =>
      mainCtaFromProps ||
      (mainCtaAction?.renderButton && mainCtaAction.renderButton(true)),
    [mainCtaFromProps, mainCtaAction]
  );

  const buttons = useMemo(
    () =>
      availableActions.filter(
        action =>
          action &&
          ['button', 'both', 'either'].includes(action.display) &&
          (mainCtaFromProps || action.key !== mainCtaAction?.key)
      ),
    [availableActions, mainCtaFromProps, mainCtaAction]
  );

  // Depending on column width (if we have it), we may need to hide some buttons
  const { showMainCta, visibleButtons } = useMemo(() => {
    // On grid layout or onlyContextMenu mode, skip buttons and send all actions to dropdown
    if (isGridLayout || onlyContextMenu) {
      return { showMainCta: false, visibleButtons: [] };
    }
    if (columnWidth) {
      const freeWidth = Math.max(
        0,
        columnWidth -
          (mainCta ? mainCtaWidthEstimate : 0) -
          BUTTON_SIZE -
          BUTTON_GAP * 2
      );
      const buttonsFitting = freeWidth > 0 ? Math.floor(freeWidth / 48) : 0;
      return {
        visibleButtons:
          buttonsFitting > 0 ? buttons.slice(-buttonsFitting) : [],
        showMainCta:
          columnWidth >= mainCtaWidthEstimate + BUTTON_SIZE + BUTTON_GAP,
      };
    }
    return { showMainCta: true, visibleButtons: buttons };
  }, [
    buttons,
    columnWidth,
    mainCta,
    isGridLayout,
    onlyContextMenu,
    mainCtaWidthEstimate,
  ]);

  const renderedButtons = useMemo(
    () =>
      visibleButtons.map(
        action => action?.renderButton && action.renderButton(false)
      ),
    [visibleButtons]
  );

  const dropdownItems = useMemo(() => {
    return (
      availableActions
        // first, select actions that can be shown in a dropdown
        // if display is 'either', we only show it if it's not visible as button/main cta
        // if display is 'mainCta-or-dropdown', it's the same as 'either' but it'll never show as button
        .filter(action => {
          const notMainCta =
            !showMainCta ||
            mainCtaFromProps ||
            mainCtaAction?.key !== action?.key;
          return (
            action?.display === 'dropdown' ||
            action?.display === 'both' ||
            (action?.display === 'mainCta-or-dropdown' && notMainCta) ||
            (action?.display === 'either' &&
              notMainCta &&
              !visibleButtons.find(button => button?.key === action.key))
          );
        })

        // then, group them by dropdownGroupIndex
        .reduce((acc, action: TableAction) => {
          const groupIndex = action.dropdownGroupIndex ?? 0;
          if (!acc[groupIndex]) {
            acc[groupIndex] = [action];
          } else {
            acc[groupIndex].push(action);
          }
          return acc;
        }, [] as TableAction[][])

        // then, sort the items by dropdownOrder and return what will be used as dropdown props
        .map((group = []) => ({
          items: group
            .sort((a, b) => (a.dropdownOrder ?? 0) - (b.dropdownOrder ?? 0))
            .reduce((acc, action) => {
              const dropdownItem = action.getDropdownItem?.();
              if (Array.isArray(dropdownItem)) {
                acc = [...acc, ...dropdownItem];
              } else if (dropdownItem) {
                acc.push(dropdownItem);
              }
              return acc;
            }, [] as NullableDropdownItem[]),
        })) as NullableDropdownGroup[]
    );
  }, [
    availableActions,
    showMainCta,
    mainCtaFromProps,
    mainCtaAction,
    visibleButtons,
  ]);

  return {
    buttons: renderedButtons,
    mainCta: showMainCta ? mainCta : null,
    dropdownItems,
  };
}
